import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { forestScene, fountainPhoto, ducksWater, bridgePhoto } from "@/assets";

export default function Blog() {
  return (
    <div className="flex flex-col pt-10">
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-foreground leading-[1.1] tracking-tight mb-8">
            Writing & Reflections.
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl text-balance">
            Thoughts on the intersection of thoughtful data analysis, intentional design, and building meaningful digital tools.
          </p>
        </div>
      </section>

      {/* Featured Essay */}
      <section className="py-12 pb-24 border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="aspect-[4/3] relative overflow-hidden rounded-sm bg-muted group cursor-pointer">
                <img 
                  src={forestScene} 
                  alt="Nature scene" 
                  className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary border border-primary/20 bg-primary/5 px-3 py-1 inline-block">
                Featured Essay
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-foreground leading-tight hover:text-primary transition-colors cursor-pointer">
                Finding the Signal: Why Thoughtful Data Analysis is More Than Dashboards
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We are drowning in data but starving for wisdom. Exploring the difference between simply reporting numbers and actually uncovering the human stories hidden within datasets.
              </p>
              <div className="pt-4">
                <a href="#" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold text-foreground hover:text-primary transition-colors group">
                  Read the Essay 
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter & Article List */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          
          <div className="flex flex-wrap gap-6 text-sm uppercase tracking-wider font-medium mb-16 border-b border-border/40 pb-6">
            <span className="text-primary border-b-2 border-primary pb-6 -mb-[25px] cursor-pointer">All Writing</span>
            <span className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors pb-6">Data Analysis</span>
            <span className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors pb-6">Digital Design</span>
            <span className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors pb-6">App Development</span>
            <span className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors pb-6">Essays</span>
          </div>

          <div className="space-y-16">
            {/* Article 1 */}
            <article className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 pb-16 border-b border-border/40 group">
              <div className="md:col-span-1">
                <div className="aspect-[4/5] bg-muted overflow-hidden rounded-sm">
                  <img 
                    src={fountainPhoto} 
                    alt="Fountain" 
                    className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              </div>
              <div className="md:col-span-3 space-y-4 flex flex-col justify-center">
                <div className="text-sm text-muted-foreground flex items-center gap-3">
                  <time dateTime="2024-10-12">Oct 12, 2024</time>
                  <span className="text-border">—</span>
                  <span className="uppercase tracking-widest text-xs font-semibold text-foreground">Website Design</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif text-foreground group-hover:text-primary transition-colors">
                  <a href="#">The Psychology of Conversion: Building Trust Before Asking for Action</a>
                </h3>
                <p className="text-muted-foreground leading-relaxed line-clamp-2">
                  Nobody converts on a website they don't trust. Analyzing the subtle visual and structural cues that signal reliability before the user ever reads your value proposition.
                </p>
                <div className="pt-4">
                  <a href="#" className="text-sm uppercase tracking-wider font-semibold text-primary/80 group-hover:text-primary transition-colors">
                    Read Article
                  </a>
                </div>
              </div>
            </article>

            {/* Article 2 */}
            <article className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 pb-16 border-b border-border/40 group">
              <div className="md:col-span-1">
                <div className="aspect-[4/5] bg-muted overflow-hidden rounded-sm">
                  <img 
                    src={ducksWater} 
                    alt="Water view" 
                    className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              </div>
              <div className="md:col-span-3 space-y-4 flex flex-col justify-center">
                <div className="text-sm text-muted-foreground flex items-center gap-3">
                  <time dateTime="2024-09-28">Sep 28, 2024</time>
                  <span className="text-border">—</span>
                  <span className="uppercase tracking-widest text-xs font-semibold text-foreground">App Development</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif text-foreground group-hover:text-primary transition-colors">
                  <a href="#">Learning to Code: Why I Stopped Following Tutorials and Started Building</a>
                </h3>
                <p className="text-muted-foreground leading-relaxed line-clamp-2">
                  The "tutorial hell" trap is real. How shifting my learning strategy from passive consumption to aggressive project-building transformed my understanding of software architecture.
                </p>
                <div className="pt-4">
                  <a href="#" className="text-sm uppercase tracking-wider font-semibold text-primary/80 group-hover:text-primary transition-colors">
                    Read Article
                  </a>
                </div>
              </div>
            </article>

            {/* Article 3 */}
            <article className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 pb-16 group">
              <div className="md:col-span-1">
                <div className="aspect-[4/5] bg-muted overflow-hidden rounded-sm">
                  <img 
                    src={bridgePhoto} 
                    alt="Bridge architecture" 
                    className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              </div>
              <div className="md:col-span-3 space-y-4 flex flex-col justify-center">
                <div className="text-sm text-muted-foreground flex items-center gap-3">
                  <time dateTime="2024-09-04">Sep 04, 2024</time>
                  <span className="text-border">—</span>
                  <span className="uppercase tracking-widest text-xs font-semibold text-foreground">Data Analysis</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif text-foreground group-hover:text-primary transition-colors">
                  <a href="#">Respecting the User: Data Privacy as a Foundation, Not an Afterthought</a>
                </h3>
                <p className="text-muted-foreground leading-relaxed line-clamp-2">
                  Ethical data analysis isn't just about compliance; it's about treating the human beings behind the data points with dignity. Structuring systems that protect by default.
                </p>
                <div className="pt-4">
                  <a href="#" className="text-sm uppercase tracking-wider font-semibold text-primary/80 group-hover:text-primary transition-colors">
                    Read Article
                  </a>
                </div>
              </div>
            </article>
          </div>

        </div>
      </section>

      {/* Explore Archive CTA */}
      <section className="bg-plum text-white py-24 border-t-4 border-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight">
            Explore the Full Archive
          </h2>
          <p className="text-lg md:text-xl text-white/80 leading-relaxed font-light">
            I publish longer essays and technical reflections regularly on Medium. Follow along to read the complete backlog.
          </p>
          <div className="pt-8">
            <a href="https://shatearadavis.medium.com/" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white rounded-none px-10 py-7 text-sm uppercase tracking-wider font-semibold">
                Read on Medium ↗
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
